import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from "vue";
import { TTEntryInput, TTDay } from "@/types";
import { parseTtEntryInputs } from "@/helpers/tt/parseTtEntryInputs";
import { validateTTNumbers } from "@/helpers/tt/validateTTNumbers";
import { validateTTNumber } from "@/helpers/tt/validateTTNumber";
import { calculateTtEntry } from "@/helpers/tt/calculateTtEntry";
import { useDialogStore } from "@/store/dialogs";
import { FEATURE_TOGGLES } from "@/config";

type NumberKey =
  | "number1"
  | "number2"
  | "number3"
  | "number4"
  | "number5"
  | "number6"
  | "number7"
  | "number8"
  | "number9"
  | "number10";


export default /*#__PURE__*/_defineComponent({
  __name: 'TTMassEntryTable2',
  props: {
    showDay: { type: Boolean, default: true },
    selectedDay: null,
    startingIndex: { default: 0 },
    value: null,
    NUMBERS_COUNT: { default: 10 },
    dayOptions: null
  },
  emits: ["focus"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: "focus", field: string, index: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { showDay: boolean, selectedDay: TTDay, startingIndex: number, value: TTEntryInput[], NUMBERS_COUNT: number, dayOptions: {
    label: string;
    value: number;
    }[] };





const vm = getCurrentInstance()?.proxy as any;
const activeRow = ref(1);
const NUMBERS_COUNT = computed(() => props.NUMBERS_COUNT);

const entries = computed(() =>
  props.value.slice(props.startingIndex, props.startingIndex + 20)
);

const rowCalculations = computed(() => {
  const validatedEntries = parseTtEntryInputs(entries.value);

  return validatedEntries.map((entry) => calculateTtEntry(entry));
});

const numbers = computed(() => {
  const result = [];
  for (let i = 1; i <= props.NUMBERS_COUNT; i++) {
    result.push(i);
  }
  return result;
});

const modeOptions = [
  { label: "", value: undefined },
  { label: "R", value: "R" },
  { label: "R2", value: "R2" },
  { label: "R3", value: "R3" },
  { label: "R4", value: "R4" },
  { label: "R5", value: "R5" },
  { label: "R7", value: "R7" },
  { label: "2*", value: "twoWild" },
  { label: "3*", value: "threeWild" },
  { label: "4*", value: "fourWild" },
  { label: "*2", value: "wild2" },
  { label: "*3", value: "wild3" },
  { label: "*4", value: "wild4" },
  { label: "TO", value: "TO" },
  { label: "TE", value: "TE" },
  { label: "TT", value: "TT" },
];

function resetNumberField(entry: TTEntryInput, index: number) {
  switch (index) {
    case 1:
      entry.number1 = "";
      break;
    case 2:
      entry.number2 = "";
      break;
    case 3:
      entry.number3 = "";
      break;
    case 4:
      entry.number4 = "";
      break;
    case 5:
      entry.number5 = "";
      break;
    case 6:
      entry.number6 = "";
      break;
    case 7:
      entry.number7 = "";
      break;
    case 8:
      entry.number8 = "";
      break;
    case 9:
      entry.number9 = "";
      break;
    case 10:
      entry.number10 = "";
      break;
  }
}

function checkNumberDuplicate(entry: TTEntryInput, number: string) {
  let sum = 0;
  if (entry.number1 === number) {
    sum++;
  }
  if (entry.number2 === number) {
    sum++;
  }
  if (entry.number3 === number) {
    sum++;
  }
  if (entry.number4 === number) {
    sum++;
  }
  if (entry.number5 === number) {
    sum++;
  }
  if (entry.number6 === number) {
    sum++;
  }
  if (entry.number7 === number) {
    sum++;
  }
  if (entry.number8 === number) {
    sum++;
  }
  if (entry.number9 === number) {
    sum++;
  }
  if (entry.number10 === number) {
    sum++;
  }
  return sum > 1;
}

function handleNumberInput(
  val: string,
  _field: "number" | "unit",
  index: number,
  numberIndex: number
) {
  const currEntry = entries.value[index];
  const number = currEntry[getNumberString(numberIndex)];
  const valid = validateTTNumber(number);

  if (val.length === 1 && !val.match(/[0-4]/)) {
    resetNumberField(currEntry, numberIndex);
    return;
  }
  if (val.length === 2 && (Number(val) < 1 || Number(val) > 49)) {
    resetNumberField(currEntry, numberIndex);
    return;
  }

  if (checkNumberDuplicate(currEntry, number)) {
    resetNumberField(currEntry, numberIndex);
    return;
  }

  if (val.match(/[0-9]/) && valid) {
    focus("number" + (numberIndex + 1), props.startingIndex + index);
  }
}

function handleKeyPresses(event: KeyboardEvent, entry: TTEntryInput) {
  handleModeInput(event, entry);
  handleDayInput(event, entry);
}

function handleEnter(
  event: KeyboardEvent,
  field: "number" | "unit" | "mode",
  index: number
) {
  const prevEntry = props.value[Math.max(index + props.startingIndex - 1, 0)];
  const currEntry = entries.value[index];

  if (event.key === "Enter") {
    switch (field) {
      case "number":
        focus("unit", index + props.startingIndex);
        break;
      case "unit":
        if (currEntry.unit === undefined || (currEntry.unit as any) === "") {
          currEntry.unit = prevEntry.unit || 1;
        }
        focus("number1", index + props.startingIndex + 1);
        break;
      case "mode":
        if (!validateTTNumbers(currEntry)) {
          return;
        }
        focus("number1", index + props.startingIndex + 1);
        break;
    }
    event.preventDefault();
    return;
  }
}

function handleUnfocus(_event: KeyboardEvent, field: "unit", index: number) {
  const currEntry = entries.value[index];
  switch (field) {
    case "unit":
      if (currEntry.unit && currEntry.unit > 500) {
        useDialogStore().addDialog({
          text: "Warning: Unit is > 500",
        });
      }
      break;
  }
}

function getNumberString(numberIndex: number): NumberKey {
  return ("number" + numberIndex) as NumberKey;
}

function handlePlusMinusKey(
  event: KeyboardEvent,
  field: "number" | "unit" | "mode",
  index: number,
  numberIndex: number
) {
  const currEntry = entries.value[index];
  if (event.key === "-") {
    switch (field) {
      case "number":
        currEntry[getNumberString(numberIndex)] = "";
        break;
      case "unit":
        currEntry.unit = undefined;
        break;
      case "mode":
        currEntry.mode = undefined;
        break;
    }
    event.preventDefault();
    return;
  } else if (event.key === "+") {
    if (index + props.startingIndex === 0) {
      return;
    }
    const prevEntry = props.value[index + props.startingIndex - 1];
    const prevValid = validateTTNumbers(prevEntry);
    if (!prevValid) {
      return;
    }
    currEntry.unit = prevEntry.unit;
    currEntry.mode = prevEntry.mode;
    const validation = validateTTNumbers(currEntry);
    event.preventDefault();
    if (!validation) {
      return;
    }
    focus("number1", index + 1 + props.startingIndex);
  }
}

function handleArrowKey(
  event: KeyboardEvent,
  field: "number" | "unit" | "mode",
  index: number,
  numberIndex: number
) {
  const key = event.key;

  let indexDiff = 0;
  let targetField = field as string;

  let triggered = false;
  const useLegacyNavigation = FEATURE_TOGGLES.useV1Legacy;

  switch (key) {
    case "ArrowLeft":
      triggered = true;
      if (field === "number") {
        if (numberIndex === 1) {
          if (useLegacyNavigation) {
            targetField = "mode";
            indexDiff--;
          } else {
            targetField = "unit";
            indexDiff--;
          }
        } else {
          targetField = "number" + (numberIndex - 1);
        }
      } else if (field === "unit") {
        targetField = "number" + props.NUMBERS_COUNT;
      } else if (field === "mode") {
        targetField = "unit";
      }
      break;
    case "ArrowRight":
      triggered = true;
      if (field === "number") {
        if (numberIndex === props.NUMBERS_COUNT) {
          targetField = "unit";
        } else {
          targetField = "number" + (numberIndex + 1);
        }
      } else if (field === "unit") {
        if (useLegacyNavigation) {
          targetField = "mode";
        } else {
          targetField = "number1";
          indexDiff++;
        }
      } else if (field === "mode" && useLegacyNavigation) {
        targetField = "number1";
        indexDiff++;
      }
      break;
    case "ArrowUp":
      triggered = true;
      indexDiff--;
      targetField = field === "number" ? "number" + numberIndex : field;
      break;
    case "ArrowDown":
      triggered = true;
      indexDiff++;
      targetField = field === "number" ? "number" + numberIndex : field;
      break;
  }
  const targetIndex = index + indexDiff;
  if (triggered) {
    event.preventDefault();
    focus(targetField, targetIndex + props.startingIndex);
  }
}

function focus(field: string, inputTargetindex: number) {
  if (
    inputTargetindex < props.startingIndex ||
    inputTargetindex >= props.startingIndex + 20
  ) {
    emit("focus", field, inputTargetindex);
    return;
  }
  const targetIndex = inputTargetindex - props.startingIndex;
  const inputElements = vm.$refs[field] as any[];
  if (!inputElements?.length) {
    return;
  }
  const inputElement = inputElements[targetIndex] as any;
  inputElement?.$el?.focus();
  if (field !== "mode") {
    inputElement?.$el?.select();
  }
  activeRow.value = inputTargetindex;
}

function handleModeInput(event: KeyboardEvent, entry: TTEntryInput) {
  const pressedRoll = ["R", "/", "r"].includes(event.key);

  if (pressedRoll) {
    if (entry.mode === "R") {
      entry.mode = undefined;
      event.preventDefault();
    } else {
      entry.mode = "R";
      event.preventDefault();
    }
  }
}

function handleDayInput(event: KeyboardEvent, entry: TTEntryInput) {
  if (!props.showDay) {
    return;
  }
  if (event.key === "*") {
    const currDay = entry.day;
    const currDayIndex = props.dayOptions.findIndex(
      (opt) => opt.value === currDay
    );
    const nextDayIndex =
      currDayIndex + 1 === props.dayOptions.length ? 0 : currDayIndex + 1;
    const nextDay = props.dayOptions[nextDayIndex];
    entry.day = nextDay.value as TTDay;
    event.preventDefault();
  }
}

expose({ focus });

return { __sfc: true,props, emit, vm, activeRow, NUMBERS_COUNT, entries, rowCalculations, numbers, modeOptions, resetNumberField, checkNumberDuplicate, handleNumberInput, handleKeyPresses, handleEnter, handleUnfocus, getNumberString, handlePlusMinusKey, handleArrowKey, focus, handleModeInput, handleDayInput, validateTTNumber }
}

})